/*------------------------------------------------------------------------------
   About      : Structured builder for `templateCriteria` (epic DA-697, II-FE-4)

                Mockup: `image-1.png` — "Advanced: template criteria", a row per
                condition over a read-only "Generated expression preview".
                Follows the mapper builder's split: the model is pure
                (`utils/criteriaBuilder.ts`, fully unit-tested) and this is a
                thin shell, the same reason that module is the best-tested code
                in the repo.

                Operators come from `criteriaGrammar.operators` and the variable
                list from `derivedQuantities` filtered to
                `usableInTemplateCriteria` — neither is hardcoded, so a change
                on quill's side needs no FE deploy.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { useMemo } from 'react'
import { Alert, Box, Button, IconButton, MenuItem, Paper, Stack, TextField,
         Tooltip, Typography } from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import DeleteOutlineIcon    from '@mui/icons-material/DeleteOutline'

import { InsightCatalog, TemplateCriteria } from '../types/insightTypes'
import { criteriaVariablesMissingFromText,
         derivedQuantitiesFor,
         displayNameFor }                   from '../utils/insightCatalogHelpers'
import { CriteriaCondition,
         conditionsToCriteria,
         formatCriteriaExpression,
         newCondition,
         validateCondition }                from '../utils/criteriaBuilder'
import { SECTION_HEADING_SX,
         SUB_HEADING_SX,
         FIELD_LABEL_SX }                   from './insightFormStyles'

interface TemplateCriteriaBuilderProps {
  conditions   : CriteriaCondition[]
  onChange     : (conditions: CriteriaCondition[]) => void
  /** Predicates the model could not parse — shown read-only, preserved on save. */
  unparsed    ?: Record<string, string>
  catalog     ?: InsightCatalog
  insightType ?: string | null
  /** The current template text, so a condition on a variable the sentence never
   *  mentions can be flagged where the author added it. */
  templateString ?: string
  disabled    ?: boolean
}

const MAX_CONDITIONS = 5

export const TemplateCriteriaBuilder = ({
  conditions,
  onChange,
  unparsed,
  catalog,
  insightType,
  templateString,
  disabled,
}: TemplateCriteriaBuilderProps) => {

  const grammar   = catalog?.criteriaGrammar
  const operators = grammar?.operators ?? []
  const atMax     = conditions.length >= MAX_CONDITIONS

  // Variables already in use are kept even if deprecated, so an existing
  // condition never loses its own variable from the dropdown.
  const variables = useMemo(
    () => derivedQuantitiesFor(catalog, {
      usage       : 'templateCriteria',
      insightType,
      keepCodes   : conditions.map((c) => c.variable).filter(Boolean),
    }),
    [catalog, insightType, conditions],
  )

  const { criteria, duplicateVariables } = useMemo(
    () => conditionsToCriteria(conditions, unparsed, grammar),
    [conditions, unparsed, grammar],
  )

  // Same rule the CAPPING tile reports, surfaced here too: this is where the
  // author creates the problem, and the tile is much further down the form.
  const missingInText = useMemo(
    () => criteriaVariablesMissingFromText(criteria, templateString, catalog),
    [criteria, templateString, catalog],
  )

  const update = (id: string, patch: Partial<CriteriaCondition>) => {
    onChange(conditions.map((c) => (c.id === id ? { ...c, ...patch } : c)))
  }

  const remove = (id: string) => onChange(conditions.filter((c) => c.id !== id))

  return (
    <Paper variant="outlined" sx={{ p : 2, mb : 2 }}>
      <Typography variant="subtitle2" sx={{ ...SECTION_HEADING_SX, mb : 0.5 }}>
        ADVANCED: TEMPLATE CRITERIA
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb : 2 }}>
        Criteria are evaluated at runtime after values are calculated. If the
        conditions are not met, the insight is hidden.
      </Typography>

      <Stack spacing={1.5}>
        {conditions.map((condition) => {
          const validation = validateCondition(condition, grammar)

          return (
            <Box key={condition.id}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  select size="small" label="Variable" disabled={disabled}
                  value={condition.variable}
                  onChange={(e) => update(condition.id, { variable : e.target.value })}
                  sx={{ ...FIELD_LABEL_SX, flexGrow : 1, minWidth : 200 }}
                >
                  {variables.map((variable) => (
                    <MenuItem key={variable.code} value={variable.code}>
                      {variable.displayName}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select size="small" label="Op" disabled={disabled}
                  value={condition.operator}
                  onChange={(e) => update(condition.id, { operator : e.target.value })}
                  sx={{ ...FIELD_LABEL_SX, width : 90 }}
                >
                  {operators.map((operator) => (
                    <MenuItem key={operator} value={operator}>{operator}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  size="small" label="Value" type="number" disabled={disabled}
                  value={condition.value}
                  onChange={(e) => update(condition.id, { value : e.target.value })}
                  sx={{ ...FIELD_LABEL_SX, width : 120 }}
                />

                {/* Second bound — optional. Adding it makes the predicate
                    compound, which is where the %s token comes in. */}
                {condition.operator2 !== undefined || condition.value2 !== undefined ? (
                  <>
                    <Typography variant="body2" sx={{ mt : 1.2, color : 'text.secondary' }}>&amp;&amp;</Typography>
                    <TextField
                      select size="small" label="Op" disabled={disabled}
                      value={condition.operator2 ?? ''}
                      onChange={(e) => update(condition.id, { operator2 : e.target.value })}
                      sx={{ ...FIELD_LABEL_SX, width : 90 }}
                    >
                      {operators.map((operator) => (
                        <MenuItem key={operator} value={operator}>{operator}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small" label="Value" type="number" disabled={disabled}
                      value={condition.value2 ?? ''}
                      onChange={(e) => update(condition.id, { value2 : e.target.value })}
                      sx={{ ...FIELD_LABEL_SX, width : 120 }}
                    />
                    <Tooltip title="Remove second bound">
                      <IconButton
                        size="small" disabled={disabled}
                        onClick={() => update(condition.id, { operator2 : undefined, value2 : undefined })}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <Button
                    size="small" disabled={disabled}
                    onClick={() => update(condition.id, { operator2 : operators[0] ?? '<', value2 : '' })}
                    sx={{ mt : 0.5, whiteSpace : 'nowrap' }}
                  >
                    + Range
                  </Button>
                )}

                <Tooltip title="Remove condition">
                  <IconButton
                    size="small" color="error" disabled={disabled}
                    onClick={() => remove(condition.id)}
                    sx={{ mt : 0.5 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

              {!validation.complete && validation.problem && (
                <Typography variant="caption" color="error" sx={{ ml : 1 }}>
                  {validation.problem}
                </Typography>
              )}
            </Box>
          )
        })}
      </Stack>

      <Button
        fullWidth
        startIcon={<AddCircleOutlineIcon />}
        disabled={disabled || atMax}
        onClick={() => onChange([...conditions, newCondition()])}
        sx={{ mt : 1.5, border : '1px dashed', borderColor : 'divider' }}
      >
        Add condition
      </Button>

      {atMax && (
        <Typography variant="caption" color="text.secondary" sx={{ display : 'block', mt : 0.5 }}>
          Maximum of {MAX_CONDITIONS} criteria conditions per insight.
        </Typography>
      )}

      {!!duplicateVariables.length && (
        <Alert severity="error" sx={{ mt : 1.5 }}>
          {duplicateVariables.join(', ')} {duplicateVariables.length === 1 ? 'is' : 'are'} used by
          more than one condition. Criteria are stored one predicate per variable,
          so only the last would survive — use a range instead.
        </Alert>
      )}

      {!!missingInText.length && (
        <Alert severity="warning" sx={{ mt : 1.5 }}>
          {missingInText.map((code) => displayNameFor(catalog, 'derivedQuantity', code)).join(', ')}
          {missingInText.length === 1 ? ' is' : ' are'} used here but not in the
          template text. Insert the matching token there — a variable that is
          constrained but never rendered has its capping applied to nothing the
          reader sees, which is what fails the capping check.
        </Alert>
      )}

      {/* ── Generated expression preview ────────────────────────────────── */}
      <Box sx={{ mt : 2 }}>
        <Typography variant="caption" sx={SUB_HEADING_SX}>
          GENERATED EXPRESSION PREVIEW
        </Typography>
        <Box
          component="pre"
          sx={{
            mt           : 0.5,
            p            : 1.5,
            bgcolor      : 'grey.900',
            color        : 'grey.100',
            fontSize     : '0.8125rem',
            borderRadius : 1,
            overflow     : 'auto',
            m            : 0,
          }}
        >
          {Object.keys(criteria).length ? JSON.stringify(criteria, null, 2) : '{}'}
        </Box>
      </Box>

      {!!Object.keys(unparsed ?? {}).length && (
        <Alert severity="info" sx={{ mt : 1.5 }}>
          {Object.keys(unparsed ?? {}).join(', ')} {Object.keys(unparsed ?? {}).length === 1 ? 'uses' : 'use'} a
          predicate this builder cannot represent. It is shown above and will be
          saved unchanged.
        </Alert>
      )}

      {conditions.some((c) => c.operator2) && (
        <Alert severity="info" sx={{ mt : 1.5 }}>
          A range emits the <code>%s</code> token, which is where the runtime value
          is substituted for the second comparison. It is part of the stored
          predicate, not a typo.
        </Alert>
      )}

      <Alert severity="info" icon={false} sx={{ mt : 1.5 }}>
        The backend validates and sandboxes every expression. No JavaScript logic
        is allowed here.
      </Alert>

      {/* Kept for the reviewer's benefit: shows the exact per-row string, which
          is what quill validates, rather than only the merged object. */}
      {conditions.some((c) => validateCondition(c, grammar).complete) && (
        <Stack spacing={0.25} sx={{ mt : 1.5 }}>
          {conditions
            .filter((c) => validateCondition(c, grammar).complete)
            .map((c) => (
              <Typography key={c.id} variant="caption" sx={{ fontFamily : 'monospace', color : 'text.secondary' }}>
                {c.variable}: {formatCriteriaExpression(c, grammar)}
              </Typography>
            ))}
        </Stack>
      )}
    </Paper>
  )
}

export default TemplateCriteriaBuilder

/** Re-exported so the edit screen can build a payload without a second import. */
export type { TemplateCriteria }
